/**
 * useTravelAI — ArcheAICapabilities for the Travel Planner page.
 *
 * This is the hook a trip-level page mounts inside <FiasAIProvider> so the
 * AI can (a) see current profile + enabled features + a compact trip
 * outline in the system prompt and (b) call tool actions for every
 * enabled feature. See useTravelAIActions for the action catalog.
 *
 * The state snapshot is read from the React Query cache rather than
 * re-fetching, so enabling a bunch of features doesn't fan out into N
 * extra network calls each time the AI renders a turn.
 */

import { useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import type { ArcheAICapabilities } from '@/components/fias-ai/types';
import { travelApi } from '../api';
import type {
  TravelerProfileFields,
  TravelUserProfileResponse,
  TravelDossierResponse,
  TravelFlightStatusFetchResponse,
  TravelItemResponse,
  TravelEventResponse,
  TravelBudgetSummaryResponse,
  TravelExpenseResponse,
  TravelTripInfoResponse,
  TravelTodoResponse,
  TravelInboxListResponse,
} from '../api';
import { queryKeys } from '@/lib/query-keys';
import { toast } from '@/components/ui/use-toast';
import { captureError } from '@/lib/telemetry';
import { useTravelUserProfile } from './useTravelUserProfile';
import { buildTravelAIActions } from './useTravelAIActions';
import { useTravelTrip } from './useTravelTrip';

export const TRAVEL_ARCHE_ID = 'arche_travel';

/**
 * @param tripId Current trip the user is viewing. Pass `undefined` from
 * list views where no single trip is active — trip-scoped actions become
 * no-ops in that case.
 */
type CreateTripParams = Parameters<typeof travelApi.createTravelTrip>[0];

export function useTravelAI(tripId?: string): ArcheAICapabilities {
  const { profile, features } = useTravelUserProfile();
  const { trip, items, events } = useTravelTrip(tripId);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const getStateSnapshot = useCallback((): Record<string, unknown> => {
    const profileEnabled = features.traveler_profile !== false;
    const flightStatusEnabled = features.flight_status !== false;
    const budgetEnabled = features.budget !== false;
    const criticEnabled = features.critic !== false;
    const packingEnabled = features.packing_list !== false;
    const mapEnabled = features.map !== false;
    const inboxEnabled = features.email_inbox !== false;

    // ------------------------------------------------------------------
    // Compact trip outline. Gives the AI the IDs + labels it needs to
    // target edits without dumping the full row. Every non-trivial action
    // takes an `itemId` or `eventId`, so without this outline the AI
    // would have to guess.
    // ------------------------------------------------------------------
    let tripOutline:
      | {
          tripId: string;
          title: string;
          startDate: string;
          items: Array<
            | {
                itemId: string;
                type: 'day';
                dayNumber: number | null;
                dayTitle: string | null;
                city: string | null;
                country: string | null;
                hasCoords: boolean;
              }
            | {
                itemId: string;
                type: 'transit';
                transitFrom: string | null;
                transitTo: string | null;
                transitMode: string | null;
                transitDeparture: string | null;
                transitArrival: string | null;
                notes: string | null;
              }
          >;
          events: Array<{
            eventId: string;
            itemId: string;
            title: string;
            time: string | null;
            eventType: string;
            reservationStatus?: string | null;
            hasCoords: boolean;
          }>;
        }
      | undefined;

    if (tripId && trip) {
      tripOutline = {
        tripId: trip.tripId,
        title: trip.title,
        startDate: trip.startDate,
        items: (items ?? []).map((i: TravelItemResponse) =>
          i.itemType === 'day'
            ? {
                itemId: i.itemId,
                type: 'day' as const,
                dayNumber: i.dayNumber,
                dayTitle: i.dayTitle,
                city: i.city,
                country: i.country,
                hasCoords: i.lat != null && i.lng != null,
              }
            : {
                itemId: i.itemId,
                type: 'transit' as const,
                transitFrom: i.transitFrom,
                transitTo: i.transitTo,
                transitMode: i.transitMode,
                transitDeparture: i.transitDeparture,
                transitArrival: i.transitArrival,
                notes: i.transitNotes,
              },
        ),
        events: (events ?? []).map((e: TravelEventResponse) => {
          const entry: {
            eventId: string;
            itemId: string;
            title: string;
            time: string | null;
            eventType: string;
            reservationStatus?: string | null;
            hasCoords: boolean;
          } = {
            eventId: e.eventId,
            itemId: e.itemId,
            title: e.title,
            time: e.time,
            eventType: e.eventType,
            hasCoords: e.lat != null && e.lng != null,
          };
          // Only surface reservation status when the scout is on, to avoid
          // bloating the outline for users who don't use that feature.
          if (features.reservation_scout !== false && e.reservationStatus) {
            entry.reservationStatus = e.reservationStatus;
          }
          return entry;
        }),
      };
    }

    // ------------------------------------------------------------------
    // Flight statuses (read from cache — the per-item query populates them)
    // ------------------------------------------------------------------
    const flightStatuses: Array<{
      itemId: string;
      flightNumber: string | null;
      status: string;
      departureGate: string | null;
      arrivalGate: string | null;
      scheduledDeparture: string | null;
      actualDeparture: string | null;
      scheduledArrival: string | null;
      actualArrival: string | null;
      lastPolledAt: string | null;
    }> = [];
    if (flightStatusEnabled) {
      const flightItems = (items ?? []).filter(
        (i: TravelItemResponse) => i.itemType === 'transit' && i.transitMode === 'Flight',
      );
      for (const item of flightItems) {
        const cached = queryClient.getQueryData<TravelFlightStatusFetchResponse | undefined>(
          queryKeys.travel.flightStatus(item.itemId),
        );
        if (cached?.status) {
          flightStatuses.push({
            itemId: item.itemId,
            flightNumber: cached.status.flightNumber,
            status: cached.status.status,
            departureGate: cached.status.departureGate,
            arrivalGate: cached.status.arrivalGate,
            scheduledDeparture: cached.status.scheduledDeparture,
            actualDeparture: cached.status.actualDeparture,
            scheduledArrival: cached.status.scheduledArrival,
            actualArrival: cached.status.actualArrival,
            lastPolledAt: cached.status.lastPolledAt,
          });
        }
      }
    }

    // ------------------------------------------------------------------
    // Budget summary (read from cache)
    // ------------------------------------------------------------------
    let budget:
      | {
          homeCurrency: string;
          totalCents: number | null;
          spentHomeCents: number;
          remainingHomeCents: number | null;
          perCategory: Array<{ category: string; amountHomeCents: number }>;
          // Compact list of the line items already in the budget. Lets the AI
          // see exactly what's covered so a "fill out the budget" request adds
          // the MISSING costs (lodging, food, transfers, activities…) instead
          // of re-seeding flights it can already see here. Capped so a long
          // ledger can't blow up the snapshot.
          lineItems: Array<{
            description: string | null;
            category: string;
            amountHomeCents: number;
            incurredAt: string;
            itemId: string | null;
            eventId: string | null;
          }>;
          lineItemsTruncated?: boolean;
        }
      | undefined;
    if (budgetEnabled && tripId) {
      const cached = queryClient.getQueryData<{ summary: TravelBudgetSummaryResponse } | undefined>(
        queryKeys.travel.budgetSummary(tripId),
      );
      if (cached?.summary) {
        const cachedExpenses = queryClient.getQueryData<
          { expenses: TravelExpenseResponse[] } | undefined
        >(queryKeys.travel.expenses(tripId));
        const allExpenses = cachedExpenses?.expenses ?? [];
        const EXPENSE_CAP = 60;
        budget = {
          homeCurrency: cached.summary.homeCurrency,
          totalCents: cached.summary.totalCents,
          spentHomeCents: cached.summary.spentHomeCents,
          remainingHomeCents: cached.summary.remainingHomeCents,
          perCategory: cached.summary.perCategory.map((c) => ({
            category: c.category,
            amountHomeCents: c.amountHomeCents,
          })),
          lineItems: allExpenses.slice(0, EXPENSE_CAP).map((e) => ({
            description: e.description,
            category: e.category,
            amountHomeCents: e.amountHomeCents,
            incurredAt: e.incurredAt,
            itemId: e.itemId,
            eventId: e.eventId,
          })),
          ...(allExpenses.length > EXPENSE_CAP ? { lineItemsTruncated: true } : {}),
        };
      }
    }

    // ------------------------------------------------------------------
    // Packing todos (read from cache, compact)
    // ------------------------------------------------------------------
    let packing:
      | {
          total: number;
          completed: number;
          items: Array<{ todoId: string; title: string; completed: boolean; category: string }>;
        }
      | undefined;
    if (packingEnabled && tripId) {
      // Pull the un-categorized list (covers all categories). Falls back to
      // the packing-scoped list if that's what's populated.
      const cachedAll = queryClient.getQueryData<{ todos: TravelTodoResponse[] } | undefined>(
        queryKeys.travel.todos(tripId),
      );
      const cachedPacking = queryClient.getQueryData<{ todos: TravelTodoResponse[] } | undefined>(
        queryKeys.travel.todos(tripId, 'packing'),
      );
      const todos = cachedAll?.todos ?? cachedPacking?.todos;
      if (todos) {
        packing = {
          total: todos.length,
          completed: todos.filter((t) => t.completed).length,
          // Cap at 50 so a huge list doesn't blow up the snapshot; the AI
          // sees enough to reference specific items.
          items: todos.slice(0, 50).map((t) => ({
            todoId: t.todoId,
            title: t.title,
            completed: t.completed,
            category: t.category,
          })),
        };
      }
    }

    // ------------------------------------------------------------------
    // Inbox summary (compact — just pending/unassigned counts so the AI
    // knows if there's anything to assign or re-parse)
    // ------------------------------------------------------------------
    let inbox: { total: number; unassigned: number; pending: number; error: number } | undefined;
    if (inboxEnabled) {
      const cached = queryClient.getQueryData<TravelInboxListResponse | undefined>(
        queryKeys.travel.inbox(),
      );
      if (cached?.emails) {
        inbox = {
          total: cached.total,
          unassigned: cached.emails.filter((e) => e.parseStatus === 'unassigned').length,
          pending: cached.emails.filter((e) => e.parseStatus === 'pending').length,
          error: cached.emails.filter((e) => e.parseStatus === 'error').length,
        };
      }
    }

    // ------------------------------------------------------------------
    // Critic signals (per-day counts, gaps, transit→first-event, walking km)
    // ------------------------------------------------------------------
    let criticSignals:
      | {
          perDayEventCounts: Array<{ dayNumber: number; count: number }>;
          gapsByDay: Array<{ dayNumber: number; gapsMinutes: number[] }>;
          transitToFirstEventMinutes: Array<{
            transitItemId: string;
            nextDayNumber: number;
            minutes: number | null;
          }>;
          totalWalkingKmByDay: Array<{ dayNumber: number; km: number }> | null;
        }
      | undefined;

    if (criticEnabled && items && items.length > 0) {
      const dayItems = items.filter((i: TravelItemResponse) => i.itemType === 'day');

      const perDayEventCounts = dayItems.map((d: TravelItemResponse) => ({
        dayNumber: d.dayNumber ?? 0,
        count: (events ?? []).filter((e: TravelEventResponse) => e.itemId === d.itemId).length,
      }));

      const parseTime = (t: string | null): number | null => {
        if (!t) return null;
        const m = t.match(/^(\d{1,2}):(\d{2})/);
        if (!m) return null;
        return parseInt(m[1], 10) * 60 + parseInt(m[2], 10);
      };

      const gapsByDay = dayItems.map((d: TravelItemResponse) => {
        const timed = (events ?? [])
          .filter((e: TravelEventResponse) => e.itemId === d.itemId)
          .map((e: TravelEventResponse) => parseTime(e.time))
          .filter((t: number | null): t is number => t !== null)
          .sort((a: number, b: number) => a - b);
        const gaps: number[] = [];
        for (let i = 1; i < timed.length; i++) {
          gaps.push(timed[i] - timed[i - 1]);
        }
        return { dayNumber: d.dayNumber ?? 0, gapsMinutes: gaps };
      });

      const transitToFirstEventMinutes: Array<{
        transitItemId: string;
        nextDayNumber: number;
        minutes: number | null;
      }> = [];
      for (let i = 0; i < items.length; i++) {
        const it = items[i];
        if (it.itemType !== 'transit') continue;
        const next = items.slice(i + 1).find((j: TravelItemResponse) => j.itemType === 'day');
        if (!next) continue;
        const arriveMin = parseTime(it.transitArrival);
        const firstEventTime = (events ?? [])
          .filter((e: TravelEventResponse) => e.itemId === next.itemId)
          .map((e: TravelEventResponse) => parseTime(e.time))
          .filter((t: number | null): t is number => t !== null)
          .sort((a: number, b: number) => a - b)[0];
        const minutes =
          arriveMin != null && firstEventTime != null ? firstEventTime - arriveMin : null;
        transitToFirstEventMinutes.push({
          transitItemId: it.itemId,
          nextDayNumber: next.dayNumber ?? 0,
          minutes,
        });
      }

      let totalWalkingKmByDay: Array<{ dayNumber: number; km: number }> | null = null;
      if (mapEnabled) {
        const haversine = (a: { lat: number; lng: number }, b: { lat: number; lng: number }) => {
          const R = 6371;
          const toRad = (d: number) => (d * Math.PI) / 180;
          const dLat = toRad(b.lat - a.lat);
          const dLng = toRad(b.lng - a.lng);
          const sa =
            Math.sin(dLat / 2) ** 2 +
            Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
          return 2 * R * Math.atan2(Math.sqrt(sa), Math.sqrt(1 - sa));
        };
        totalWalkingKmByDay = dayItems.map((d: TravelItemResponse) => {
          const coords = (events ?? [])
            .filter((e: TravelEventResponse) => e.itemId === d.itemId)
            .sort((a: TravelEventResponse, b: TravelEventResponse) => a.sortOrder - b.sortOrder)
            .map((e: TravelEventResponse) =>
              e.lat != null && e.lng != null ? { lat: e.lat, lng: e.lng } : null,
            )
            .filter(
              (
                c: { lat: number; lng: number } | null,
              ): c is {
                lat: number;
                lng: number;
              } => c !== null,
            );
          let km = 0;
          for (let i = 1; i < coords.length; i++) {
            km += haversine(coords[i - 1], coords[i]);
          }
          return { dayNumber: d.dayNumber ?? 0, km: Math.round(km * 10) / 10 };
        });
      }

      criticSignals = {
        perDayEventCounts,
        gapsByDay,
        transitToFirstEventMinutes,
        totalWalkingKmByDay,
      };
    }

    // Trip Info — traveler-set context that steers everything the AI generates.
    let tripInfo: Record<string, unknown> | undefined;
    if (tripId) {
      const cached = queryClient.getQueryData<{ info: TravelTripInfoResponse | null } | undefined>(
        queryKeys.travel.tripInfo(tripId),
      );
      const i = cached?.info;
      if (i) {
        const entries: Record<string, unknown> = {};
        if (i.partySize != null) entries.partySize = i.partySize;
        if (i.relationship?.length) entries.relationship = i.relationship;
        if (i.interests?.length) entries.interests = i.interests;
        if (i.budgetLevel) entries.budgetLevel = i.budgetLevel;
        if (i.hardBudgetCents != null) entries.hardBudgetCents = i.hardBudgetCents;
        if (i.pace) entries.pace = i.pace;
        if (i.dietary?.length) entries.dietary = i.dietary;
        if (i.notes) entries.notes = i.notes;
        if (Object.keys(entries).length > 0) tripInfo = entries;
      }
    }

    return {
      features,
      // Profile only surfaces when the feature is on.
      ...(profileEnabled ? { profile } : {}),
      ...(tripInfo ? { tripInfo } : {}),
      ...(tripOutline ? { tripOutline } : {}),
      ...(flightStatusEnabled && flightStatuses.length > 0 ? { flightStatuses } : {}),
      ...(budgetEnabled && budget ? { budget } : {}),
      ...(packingEnabled && packing ? { packing } : {}),
      ...(inboxEnabled && inbox ? { inbox } : {}),
      ...(criticSignals ? { criticSignals } : {}),
    };
  }, [profile, features, trip, items, events, queryClient, tripId]);

  // ======================================================================
  // applyAction — invoked when the user approves a proposal from the AI
  // ======================================================================
  const applyAction = useCallback(
    (actionId: string, params: Record<string, unknown>) => {
      /**
       * Invalidates every trip-level cache key the AI might have touched.
       * Used by core trip/item/event mutations that can ripple across
       * the detail, summary, inbox, and share views.
       */
      const invalidateTrip = (id?: string | null) => {
        const target = id ?? tripId;
        if (!target) return;
        void queryClient.invalidateQueries({ queryKey: queryKeys.travel.detail(target) });
        void queryClient.invalidateQueries({ queryKey: queryKeys.travel.my() });
        void queryClient.invalidateQueries({
          queryKey: queryKeys.travel.budgetSummary(target),
        });
        void queryClient.invalidateQueries({
          queryKey: queryKeys.travel.expenses(target),
        });
      };

      /**
       * Terminal .catch for the fire-and-forget action chains below. Without
       * it a failed mutation is an unhandled rejection with no cache
       * invalidation and no user feedback — the AI (and the user) believe
       * the action was applied when it wasn't.
       */
      const onAIActionError = (err: unknown) => {
        captureError(err instanceof Error ? err : new Error(String(err)), {
          component: 'useTravelAI',
          action: actionId,
        });
        toast({
          description: "Sorry — that action didn't go through. Please try again.",
        });
      };

      switch (actionId) {
        // -----------------------------------------------------------
        // Profile + global toggle
        // -----------------------------------------------------------
        case 'update_profile': {
          const patch = params as Partial<TravelerProfileFields>;
          void travelApi
            .updateTravelUserProfile(patch)
            .then((res) => {
              queryClient.setQueryData<TravelUserProfileResponse | undefined>(
                queryKeys.travel.profile(),
                (prev) =>
                  prev
                    ? { ...prev, profile: res.profile, updatedAt: res.updatedAt }
                    : { profile: res.profile, features: {}, updatedAt: res.updatedAt },
              );
            })
            .catch(onAIActionError);
          break;
        }
        case 'toggle_feature': {
          const key = params.key as string | undefined;
          const enabled = params.enabled as boolean | undefined;
          if (!key || typeof enabled !== 'boolean') return;
          void travelApi
            .setTravelFeature(key, enabled)
            .then((res) => {
              queryClient.setQueryData<TravelUserProfileResponse | undefined>(
                queryKeys.travel.profile(),
                (prev) => (prev ? { ...prev, features: res.features } : prev),
              );
            })
            .catch(onAIActionError);
          break;
        }

        // -----------------------------------------------------------
        // Core trip CRUD
        // -----------------------------------------------------------
        case 'create_trip': {
          const payload: CreateTripParams = {
            title: params.title as string | undefined,
            startDate: params.startDate as string | undefined,
            initialDays: params.initialDays as CreateTripParams['initialDays'],
            initialTransits: params.initialTransits as CreateTripParams['initialTransits'],
          };
          void travelApi
            .createTravelTrip(payload)
            .then((res) => {
              // Seed the detail-query cache from the server's full response so
              // the planner page renders the populated trip immediately on
              // navigation. Without this seed the destination remounts with
              // empty data and the user has to refresh to see the days/events.
              if (res.trip?.tripId) {
                queryClient.setQueryData(queryKeys.travel.detail(res.trip.tripId), {
                  trip: res.trip,
                  items: res.items ?? [],
                  events: res.events ?? [],
                  attachments: res.attachments ?? [],
                });
              }
              void queryClient.invalidateQueries({ queryKey: queryKeys.travel.my() });
              // Mirror useTravelListAI: surface partially-applied creates so the
              // user knows some initial days/transits were dropped.
              if (res.skippedCommands?.length) {
                toast({
                  description: `Trip created — but ${res.skippedCommands.length} item(s) couldn't be added and were skipped.`,
                });
              }
              if (res.trip?.tripId) {
                navigate(`/a/arche_travel/${res.trip.tripId}`);
              }
            })
            .catch(onAIActionError);
          break;
        }
        case 'update_trip': {
          if (!tripId) return;
          void travelApi
            .updateTravelTrip(tripId, {
              title: params.title as string | undefined,
              startDate: params.startDate as string | undefined,
              status: params.status as 'active' | 'archived' | undefined,
            })
            .then(() => invalidateTrip())
            .catch(onAIActionError);
          break;
        }
        case 'delete_trip': {
          if (!tripId) return;
          void travelApi
            .deleteTravelTrip(tripId)
            .then(() => {
              void queryClient.invalidateQueries({ queryKey: queryKeys.travel.my() });
              void queryClient.invalidateQueries({ queryKey: queryKeys.travel.details() });
            })
            .catch(onAIActionError);
          break;
        }

        // -----------------------------------------------------------
        // Days + Transits
        // -----------------------------------------------------------
        case 'add_day': {
          if (!tripId) return;
          void travelApi
            .createTravelDay(tripId, params as Record<string, unknown>)
            .then(() => invalidateTrip())
            .catch(onAIActionError);
          break;
        }
        case 'add_transit': {
          if (!tripId) return;
          void travelApi
            .createTravelTransit(tripId, params as Record<string, unknown>)
            .then(() => invalidateTrip())
            .catch(onAIActionError);
          break;
        }
        case 'update_item': {
          const itemId = params.itemId as string | undefined;
          if (!itemId) return;
          const { itemId: _drop, ...rest } = params as Record<string, unknown>;
          void travelApi
            .updateTravelItem(itemId, rest)
            .then(() => invalidateTrip())
            .catch(onAIActionError);
          break;
        }
        case 'delete_item': {
          const itemId = params.itemId as string | undefined;
          if (!itemId) return;
          void travelApi
            .deleteTravelItem(itemId)
            .then(() => invalidateTrip())
            .catch(onAIActionError);
          break;
        }
        case 'duplicate_day': {
          const itemId = params.itemId as string | undefined;
          if (!itemId) return;
          void travelApi
            .duplicateTravelDay(itemId)
            .then(() => invalidateTrip())
            .catch(onAIActionError);
          break;
        }
        case 'reorder_items': {
          if (!tripId) return;
          const itemIds = params.itemIds as string[] | undefined;
          if (!Array.isArray(itemIds) || itemIds.length === 0) return;
          void travelApi
            .reorderTravelItems(tripId, itemIds)
            .then(() => invalidateTrip())
            .catch(onAIActionError);
          break;
        }

        // -----------------------------------------------------------
        // Events
        // -----------------------------------------------------------
        case 'add_event': {
          const itemId = params.itemId as string | undefined;
          if (!itemId) return;
          const { itemId: _drop, ...rest } = params as Record<string, unknown>;
          void travelApi
            .createTravelEvent(itemId, rest)
            .then(() => invalidateTrip())
            .catch(onAIActionError);
          break;
        }
        case 'update_event': {
          const eventId = params.eventId as string | undefined;
          if (!eventId) return;
          const { eventId: _drop, ...rest } = params as Record<string, unknown>;
          void travelApi
            .updateTravelEvent(eventId, rest)
            .then(() => invalidateTrip())
            .catch(onAIActionError);
          break;
        }
        case 'delete_event': {
          const eventId = params.eventId as string | undefined;
          if (!eventId) return;
          void travelApi
            .deleteTravelEvent(eventId)
            .then(() => invalidateTrip())
            .catch(onAIActionError);
          break;
        }
        case 'move_event': {
          const eventId = params.eventId as string | undefined;
          const targetItemId = params.targetItemId as string | undefined;
          if (!eventId || !targetItemId) return;
          void travelApi
            .moveTravelEvent(eventId, targetItemId)
            .then(() => invalidateTrip())
            .catch(onAIActionError);
          break;
        }
        case 'duplicate_event': {
          const eventId = params.eventId as string | undefined;
          const targetItemId = params.targetItemId as string | undefined;
          if (!eventId || !targetItemId) return;
          void travelApi
            .duplicateTravelEvent(eventId, targetItemId)
            .then(() => invalidateTrip())
            .catch(onAIActionError);
          break;
        }
        case 'reorder_events': {
          const itemId = params.itemId as string | undefined;
          const eventIds = params.eventIds as string[] | undefined;
          if (!itemId || !Array.isArray(eventIds)) return;
          void travelApi
            .reorderTravelEvents(itemId, eventIds)
            .then(() => invalidateTrip())
            .catch(onAIActionError);
          break;
        }

        // -----------------------------------------------------------
        // Map (F3) — geocoding
        // -----------------------------------------------------------
        case 'geocode_item': {
          const itemId = params.itemId as string | undefined;
          const q = params.query as string | undefined;
          if (!itemId || !q) return;
          void travelApi
            .geocodeTravelItem(itemId, q)
            .then(() => invalidateTrip())
            .catch(onAIActionError);
          break;
        }
        case 'geocode_event': {
          const eventId = params.eventId as string | undefined;
          const q = params.query as string | undefined;
          if (!eventId || !q) return;
          void travelApi
            .geocodeTravelEvent(eventId, q)
            .then(() => invalidateTrip())
            .catch(onAIActionError);
          break;
        }

        // -----------------------------------------------------------
        // Dossier (F3)
        // -----------------------------------------------------------
        case 'regenerate_dossier': {
          if (!tripId) return;
          void travelApi
            .generateTripDossier(tripId)
            .then((res) => {
              queryClient.setQueryData<{ dossier: TravelDossierResponse | null } | undefined>(
                queryKeys.travel.dossier(tripId),
                { dossier: res.dossier },
              );
            })
            .catch(onAIActionError);
          break;
        }

        // -----------------------------------------------------------
        // Email inbox (F2)
        // -----------------------------------------------------------
        case 'reparse_email': {
          const emailId = params.emailId as string | undefined;
          if (!emailId) return;
          void travelApi
            .reparseTravelInboundEmail(emailId)
            .then(() => {
              void queryClient.invalidateQueries({ queryKey: queryKeys.travel.inbox() });
              if (tripId) invalidateTrip();
            })
            .catch(onAIActionError);
          break;
        }
        case 'assign_email_to_trip': {
          const emailId = params.emailId as string | undefined;
          const targetTripId = params.tripId as string | undefined;
          if (!emailId || !targetTripId) return;
          void travelApi
            .assignTravelInboundEmailToTrip(emailId, targetTripId)
            .then(() => {
              void queryClient.invalidateQueries({ queryKey: queryKeys.travel.inbox() });
              invalidateTrip(targetTripId);
            })
            .catch(onAIActionError);
          break;
        }

        // -----------------------------------------------------------
        // Flight status (F5)
        // -----------------------------------------------------------
        case 'get_flight_status': {
          const itemId = params.itemId as string | undefined;
          if (!itemId) return;
          void travelApi
            .getTravelFlightStatus(itemId, { force: true })
            .then((res) => {
              queryClient.setQueryData<TravelFlightStatusFetchResponse | undefined>(
                queryKeys.travel.flightStatus(itemId),
                res,
              );
            })
            .catch(() => {
              // Swallow — the AI will see absent status on the next read.
            });
          break;
        }

        // -----------------------------------------------------------
        // Budget + Expenses (F7)
        // -----------------------------------------------------------
        case 'update_budget': {
          if (!tripId) return;
          const homeCurrency = params.homeCurrency as string | undefined;
          // Preserve the "null clears the cap" semantics — distinguish
          // explicit null from omitted.
          const totalCentsRaw = (params as Record<string, unknown>).totalCents;
          const input: { homeCurrency?: string; totalCents?: number | null } = {};
          if (homeCurrency) input.homeCurrency = homeCurrency;
          if (totalCentsRaw === null) input.totalCents = null;
          else if (typeof totalCentsRaw === 'number') input.totalCents = totalCentsRaw;
          void travelApi
            .updateTravelBudget(tripId, input)
            .then(() => {
              void queryClient.invalidateQueries({ queryKey: queryKeys.travel.budget(tripId) });
              void queryClient.invalidateQueries({
                queryKey: queryKeys.travel.budgetSummary(tripId),
              });
            })
            .catch(onAIActionError);
          break;
        }
        case 'add_expense': {
          if (!tripId) return;
          void travelApi
            .createTravelExpense(tripId, params)
            .then(() => {
              void queryClient.invalidateQueries({ queryKey: queryKeys.travel.expenses(tripId) });
              void queryClient.invalidateQueries({
                queryKey: queryKeys.travel.budgetSummary(tripId),
              });
            })
            .catch(onAIActionError);
          break;
        }
        case 'update_expense': {
          const expenseId = params.expenseId as string | undefined;
          if (!expenseId) return;
          const { expenseId: _drop, ...rest } = params as Record<string, unknown>;
          void travelApi
            .updateTravelExpense(expenseId, rest)
            .then(() => {
              if (!tripId) return;
              void queryClient.invalidateQueries({ queryKey: queryKeys.travel.expenses(tripId) });
              void queryClient.invalidateQueries({
                queryKey: queryKeys.travel.budgetSummary(tripId),
              });
            })
            .catch(onAIActionError);
          break;
        }
        case 'delete_expense': {
          const expenseId = params.expenseId as string | undefined;
          if (!expenseId) return;
          void travelApi
            .deleteTravelExpense(expenseId)
            .then(() => {
              if (!tripId) return;
              void queryClient.invalidateQueries({ queryKey: queryKeys.travel.expenses(tripId) });
              void queryClient.invalidateQueries({
                queryKey: queryKeys.travel.budgetSummary(tripId),
              });
            })
            .catch(onAIActionError);
          break;
        }

        // -----------------------------------------------------------
        // Packing / Todos (F6)
        // -----------------------------------------------------------
        case 'generate_packing_list': {
          if (!tripId) return;
          void travelApi
            .generateTravelPackingList(tripId)
            .then(() => {
              void queryClient.invalidateQueries({
                queryKey: queryKeys.travel.todosByTrip(tripId),
              });
            })
            .catch(onAIActionError);
          break;
        }
        case 'add_todo': {
          if (!tripId) return;
          const title = params.title as string | undefined;
          if (!title) return;
          void travelApi
            .createTravelTodo(tripId, {
              title,
              notes: (params.notes as string | null | undefined) ?? null,
              category: (params.category as string | undefined) ?? 'packing',
            })
            .then(() => {
              void queryClient.invalidateQueries({
                queryKey: queryKeys.travel.todosByTrip(tripId),
              });
            })
            .catch(onAIActionError);
          break;
        }
        case 'toggle_todo': {
          const todoId = params.todoId as string | undefined;
          const completed = params.completed as boolean | undefined;
          if (!todoId || typeof completed !== 'boolean') return;
          void travelApi
            .updateTravelTodo(todoId, { completed })
            .then(() => {
              if (!tripId) return;
              void queryClient.invalidateQueries({
                queryKey: queryKeys.travel.todosByTrip(tripId),
              });
            })
            .catch(onAIActionError);
          break;
        }
        case 'delete_todo': {
          const todoId = params.todoId as string | undefined;
          if (!todoId) return;
          void travelApi
            .deleteTravelTodo(todoId)
            .then(() => {
              if (!tripId) return;
              void queryClient.invalidateQueries({
                queryKey: queryKeys.travel.todosByTrip(tripId),
              });
            })
            .catch(onAIActionError);
          break;
        }

        // -----------------------------------------------------------
        // Reservation scout (F9)
        // -----------------------------------------------------------
        case 'scout_reservations': {
          if (!tripId) return;
          const scope = (params.scope as 'trip' | 'day' | 'event' | undefined) ?? 'trip';
          const itemId = params.itemId as string | undefined;
          const eventId = params.eventId as string | undefined;
          void travelApi
            .runTravelReservationScout(tripId, { scope, itemId, eventId })
            .then(() => invalidateTrip())
            .catch(onAIActionError);
          break;
        }

        case 'scout_flight_fares': {
          if (!tripId) return;
          const cabin = params.cabin as
            | 'economy'
            | 'premium_economy'
            | 'business'
            | 'first'
            | undefined;
          const travelers = typeof params.travelers === 'number' ? params.travelers : undefined;
          void travelApi
            .runTravelFlightFareScout(tripId, { cabin, travelers })
            .then(() => invalidateTrip())
            .catch(onAIActionError);
          break;
        }

        // -----------------------------------------------------------
        // Trip critic (F4)
        // -----------------------------------------------------------
        case 'run_critic': {
          if (!tripId) return;
          // Fire and forget — the Trip Analysis panel renders the output
          // when the user opens it. The AI only needs to know the run
          // completed (which it will on the next message).
          void travelApi.runTravelCritic(tripId).catch(() => {
            // Swallow — 502s etc. surface to the user via the panel.
          });
          break;
        }
      }
    },
    [queryClient, tripId, navigate],
  );

  return useMemo(
    () => ({
      archeId: TRAVEL_ARCHE_ID,
      enabled: true,
      // Single shared session across the whole travel arche so a "plan me a
      // trip" conversation started on the list page still shows up after we
      // navigate into the new trip. Same key as useTravelListAI.
      sessionKey: 'arche_travel',
      archeDescription:
        'Travel Planner arche. Helps users build multi-day trip itineraries with days, transit ' +
        'segments, events, and attachments. Supports dossier research, map pins, flight status, ' +
        'multi-currency budgeting, AI packing lists, reservation scouting, trip-analysis critic, ' +
        'email-forward ingestion, and shareable public links. The AI has tools for every core ' +
        'operation — creating/editing/deleting trips, days, events — plus every feature above.',
      // Inside a trip-detail view, hide create_trip so the AI can't "fill out
      // this trip" by silently spawning a brand-new trip and dropping the user's
      // active context — the request is always about the open trip; use
      // add_day / add_event instead. List-view (no tripId) keeps create_trip.
      actions: tripId
        ? buildTravelAIActions(features).filter((a) => a.id !== 'create_trip')
        : buildTravelAIActions(features),
      getStateSnapshot,
      applyAction,
    }),
    [features, tripId, getStateSnapshot, applyAction],
  );
}
